import { useEffect, useState, type FormEvent } from 'react';
import { usePlaces } from '@/hooks/usePlaces';
import { useContacts } from '@/hooks/useContacts';
import { useRentalInfo } from '@/hooks/useRentalInfo';
import { useAuth } from '@/app/AuthContext';
import { api, ApiError } from '@/lib/api';
import { relativeVerifiedLabel } from '@/lib/formatting';

function WriteUnlock() {
  const { login } = useAuth();
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState<'unknown' | 'unlocked' | 'locked'>('unknown');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get<{ authenticated: boolean }>('/auth/session')
      .then((res) => setStatus(res.authenticated ? 'unlocked' : 'locked'))
      .catch(() => setStatus('locked'));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(pin);
      setStatus('unlocked');
      setPin('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (err instanceof ApiError) {
        setError(err.message || 'Incorrect PIN.');
      } else {
        setError('Could not unlock writes.');
      }
      setPin('');
    } finally {
      setSubmitting(false);
    }
  }

  if (status === 'unknown') return null;
  if (status === 'unlocked') {
    return (
      <div className="card">
        <p style={{ fontSize: 14, color: 'var(--color-ink-muted)', margin: 0 }}>
          Writes unlocked on this device. Neighbours browsing the app cannot change places,
          contacts, or house info.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Unlock writes</h2>
      <p style={{ fontSize: 14, color: 'var(--color-ink-muted)', lineHeight: 1.5 }}>
        Browsing stays public. Saving changes here needs the family PIN so unauthenticated
        callers cannot rewrite the directory.
      </p>
      <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
        <div className="field">
          <label htmlFor="admin-pin">Family PIN</label>
          <input
            id="admin-pin"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            className="pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            maxLength={10}
            required
          />
        </div>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block" disabled={submitting || pin.length === 0}>
          {submitting ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}

function PlacesAdmin() {
  const { places, loading } = usePlaces();

  async function markVerified(id: number) {
    await api.post(`/admin/places/${id}/verify`);
    window.location.reload();
  }

  async function toggleActive(id: number, isActive: boolean) {
    await api.put(`/admin/places/${id}/active`, { is_active: !isActive });
    window.location.reload();
  }

  return (
    <div className="card">
      <h2>Places ({places?.length ?? 0})</h2>
      {loading && <p>Loading…</p>}
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {places?.map((p) => (
          <div key={p.id} className="list-row">
            <span>
              {p.name}
              <br />
              <small style={{ color: 'var(--color-ink-muted)' }}>
                {p.category} · {relativeVerifiedLabel(p.last_verified_at)} · {p.is_active ? 'Active' : 'Closed/Hidden'}
              </small>
            </span>
            <span style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => markVerified(p.id)}>
                Verify
              </button>
              <button type="button" className="btn btn-sm btn-danger" onClick={() => toggleActive(p.id, p.is_active)}>
                {p.is_active ? 'Mark Closed' : 'Reactivate'}
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactsAdmin() {
  const { contacts, loading } = useContacts();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [category, setCategory] = useState('household');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/admin/contacts', { name, role, category, phone: phone || null, email: email || null });
      window.location.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save contact.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <h2>Household Contacts ({contacts.length})</h2>
      {loading && <p>Loading…</p>}
      {contacts.map((c) => (
        <div key={c.id} className="list-row">
          <span>
            {c.name}
            <br />
            <small style={{ color: 'var(--color-ink-muted)' }}>
              {c.role} · {c.category}
            </small>
          </span>
        </div>
      ))}
      <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
        <div className="section-title">Add Contact</div>
        <div className="field">
          <label htmlFor="contact-name">Name</label>
          <input id="contact-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="contact-role">Role</label>
          <input id="contact-role" type="text" value={role} onChange={(e) => setRole(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="contact-category">Category</label>
          <select id="contact-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="realtor">Realtor</option>
            <option value="landlord">Landlord</option>
            <option value="property_manager">Property Manager</option>
            <option value="maintenance">Maintenance</option>
            <option value="babcock_national">Babcock National</option>
            <option value="household">Household</option>
            <option value="emergency">Emergency</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="contact-phone">Phone</label>
          <input id="contact-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="contact-email">Email</label>
          <input id="contact-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        {error && <p className="error-text">{error}</p>}
        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          Add Contact
        </button>
      </form>
    </div>
  );
}

function RentalInfoAdmin() {
  const { info } = useRentalInfo();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put('/admin/rental-info', { key, label, value });
      window.location.reload();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <h2>House Information ({info.length})</h2>
      {info.map((i) => (
        <div key={i.key} className="list-row">
          <span>{i.label}</span>
          <span>{i.value}</span>
        </div>
      ))}
      <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
        <div className="section-title">Add / Update</div>
        <div className="field">
          <label htmlFor="ri-key">Key (e.g. wifi, gate, trash)</label>
          <input id="ri-key" type="text" value={key} onChange={(e) => setKey(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="ri-label">Label</label>
          <input id="ri-label" type="text" value={label} onChange={(e) => setLabel(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="ri-value">Value</label>
          <textarea id="ri-value" value={value} onChange={(e) => setValue(e.target.value)} required />
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          Save
        </button>
      </form>
    </div>
  );
}

function NoticesAdmin() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('normal');
  const [area, setArea] = useState('babcock_ranch');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/admin/notices', { title, body, severity, source_area: area });
      setTitle('');
      setBody('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <h2>Post a Community Notice</h2>
      <form onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="notice-title">Title</label>
          <input id="notice-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="notice-body">Details</label>
          <textarea id="notice-body" value={body} onChange={(e) => setBody(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="notice-severity">Severity</label>
          <select id="notice-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="normal">Normal</option>
            <option value="alert">Alert</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="notice-area">Area</label>
          <select id="notice-area" value={area} onChange={(e) => setArea(e.target.value)}>
            <option value="babcock_ranch">Babcock Ranch</option>
            <option value="babcock_national">Babcock National</option>
            <option value="household">Household</option>
          </select>
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          Post Notice
        </button>
      </form>
    </div>
  );
}

export function AdminPage() {
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Admin</h1>
        <p className="page-subtitle">Keep the family's information current</p>
      </div>
      <WriteUnlock />
      <PlacesAdmin />
      <ContactsAdmin />
      <RentalInfoAdmin />
      <NoticesAdmin />
    </div>
  );
}
